import React, { useContext, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

import { GetCouponByCMS } from '@/services/CMSApi'
import { fetchReferrerMetadata } from '@/utils/fetchReferrerMetadata'
import {
  getSessionSearchParams,
  getRealTimeSearchParams
} from '@/utils/searchParams'

interface Context {
  referrerTierID?: string
  couponPercentApplied?: number
  couponAppliedDiscountModal: boolean
  setCouponAppliedDiscountModal: (value: boolean) => void
}

const CouponContext = React.createContext({} as Context)

export function useCouponContext() {
  return useContext(CouponContext)
}

export const CouponContextProvider = ({
  children
}: {
  children: React.ReactNode
}) => {
  const router = useRouter()
  const { executeRecaptcha } = useGoogleReCaptcha()

  const [referrerTierID, setReferrerTierID] = useState<string>()
  const [couponPercentApplied, setCouponPercentApplied] = useState<
    number | undefined
  >()

  const [couponAppliedDiscountModal, setCouponAppliedDiscountModal] =
    useState(false)

  async function getRecaptchaToken() {
    if (!executeRecaptcha) return

    const token = await executeRecaptcha()

    if (!token) return

    return token
  }

  useEffect(() => {
    const sessionSearchParams = getSessionSearchParams() || {}
    const realTimeSearchParams = getRealTimeSearchParams() || {}

    const params = {
      ...sessionSearchParams,
      ...realTimeSearchParams
    }

    const coupon =
      params.coupon ||
      params.referrer ||
      router.query.coupon ||
      router.query.referrer

    if (!coupon) return

    async function fetchCouponOrReferrer() {
      const couponCMS = await GetCouponByCMS(coupon)

      if (couponCMS?.percentDiscount) {
        setCouponPercentApplied(Number.parseInt(couponCMS.percentDiscount))
        return
      }

      const recaptchaToken = await getRecaptchaToken()

      if (!recaptchaToken) return

      const response = await fetchReferrerMetadata({
        coupon: coupon as string,
        deviceToken: recaptchaToken
      })

      if (response?.offer?.value) {
        setCouponPercentApplied(Number.parseInt(response.offer.value))
      }

      if (response?.tier_id) {
        setReferrerTierID(response.tier_id)
      }
    }

    fetchCouponOrReferrer()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executeRecaptcha, router.query.coupon, router.query.referrer])

  return (
    <CouponContext.Provider
      value={{
        referrerTierID,
        couponPercentApplied,
        couponAppliedDiscountModal,
        setCouponAppliedDiscountModal
      }}
    >
      {children}
    </CouponContext.Provider>
  )
}
